import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LogOut } from "lucide-react";

export default function DashboardShell({ nav, title }) {
  // placeholder - real shell rendered via Layout below
  return null;
}

export function Shell({ nav, children, portalLabel }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const SideContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-5 h-16 border-b border-slate-800">
        <div className="h-9 w-9 rounded-xl bg-blue-600 grid place-items-center font-heading font-extrabold text-white">V</div>
        <div>
          <div className="font-heading font-extrabold tracking-tight leading-none">NaxoPay</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{portalLabel}</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={n.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                isActive ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20" : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px]" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="px-3 py-2 mb-1">
          <div className="text-sm font-medium text-white truncate">{user?.name}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
        </div>
        <button onClick={doLogout} data-testid="logout-btn" className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:bg-rose-500/10 hover:text-rose-400 transition-all">
          <LogOut className="h-[18px] w-[18px]" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-slate-900 border-r border-slate-800 z-40">
        <SideContent />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between h-14 px-4 bg-slate-900 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-blue-600 grid place-items-center font-heading font-extrabold text-white text-sm">V</div>
          <span className="font-heading font-bold text-white">NaxoPay</span>
        </div>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" className="text-white p-2"><Menu className="h-5 w-5" /></button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-slate-900 border-r border-slate-800">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-slate-400 z-10"><X className="h-5 w-5" /></button>
            <SideContent />
          </div>
        </div>
      )}

      <main className="lg:pl-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">{children}</div>
      </main>
    </div>
  );
}
